// tslint:disable:variable-name
import { HttpClient, HttpParams, HttpHeaders } from "@angular/common/http";
import { ActivatedRoute, Router } from "@angular/router";
import { BehaviorSubject, Observable, of, Subscription } from "rxjs";
import { finalize, tap, map } from "rxjs/operators";
import { PaginatorState } from "../models/paginator.model";
import { ITableState, TableResponseModel } from "../models/table.model";
import { BaseModel } from "../models/base.model";
import { SortState } from "../models/sort.model";
import { RequestTypes } from "../models/request-types.enum";
import { tableStateParams } from "src/app/settings";

const DEFAULT_STATE: ITableState = {
  filter: {},
  paginator: new PaginatorState(),
  sorting: new SortState(),
  searchTerm: "",
};

export abstract class TableService<T> {
  // Private fields
  private _items$ = new BehaviorSubject<T[]>([]);
  private _item$ = new BehaviorSubject<T>(null);
  private _totalRecords$ = new BehaviorSubject<number>(0);
  public _isLoading$ = new BehaviorSubject<boolean>(false);
  public _isFirstLoading$ = new BehaviorSubject<boolean>(false);
  public _isRequestLoading$ = new BehaviorSubject<boolean>(false);
  private _tableState$ = new BehaviorSubject<ITableState>(DEFAULT_STATE);
  private _subscriptions: Subscription[] = [];

  // Getters
  get items$() {
    return this._items$.asObservable();
  }

  get item$() {
    return this._item$.asObservable();
  }

  get totalRecords$() {
    return this._totalRecords$.asObservable();
  }
  get isLoading$() {
    return this._isLoading$.asObservable();
  }
  get isFirstLoading$() {
    return this._isFirstLoading$.asObservable();
  }

  get isRequestLoading$() {
    return this._isRequestLoading$.asObservable();
  }

  get subscriptions() {
    return this._subscriptions;
  }
  // State getters
  get paginator() {
    return this._tableState$.value.paginator;
  }
  get filter() {
    return this._tableState$.value.filter;
  }
  get sorting() {
    return this._tableState$.value.sorting;
  }
  get searchTerm() {
    return this._tableState$.value.searchTerm;
  }

  protected http: HttpClient;
  public router: Router;
  public activatedRoute: ActivatedRoute;

  httpOptions = {
    headers: new HttpHeaders({
      "Content-Type": "application/json",
    }),
  };

  // API URL has to be overrided
  BASE_URL = "";
  GET_ALL_URL = "";
  GET_ONE_URL = "";
  CREATE_URL = "";
  UPDATE_URL = "";
  DELETE_URL = "";
  DEFAULT_SORT_FIELD = "";

  constructor(
    http: HttpClient,
    router: Router,
    activatedRoute: ActivatedRoute
  ) {
    this.http = http;
    this.router = router;
    this.activatedRoute = activatedRoute;
  }

  public fetch(
    {
      queryParams = null,
      changeRoute = true,
    }: { queryParams?: any; changeRoute?: boolean } = {
      queryParams: null,
      changeRoute: true,
    }
  ) {
    let params = new HttpParams();

    if (queryParams) {
      Object.entries(queryParams).forEach((item: any) => {
        params = params.append(item[0], item[1]);
      });
    }

    params = params.append("pageSize", this.paginator.pageSize.toString());
    params = params.append("pageNumber", this.paginator.page.toString());
    params = params.append("sortField", this.sorting.column);
    params = params.append("sortOrder", this.sorting.direction);

    console.log("------------ Search Term ------------", this.searchTerm);

    if (this.searchTerm) {
      params = params.append("search", this.searchTerm);
    }

    console.log(params);

    const url = `${this.BASE_URL}/${this.GET_ALL_URL}`;
    this._isLoading$.next(true);
    const request = this.http
      .get<TableResponseModel<T>>(url, { params })
      .pipe(
        tap((res: TableResponseModel<T>) => {
          this._items$.next(res.items);
          this._totalRecords$.next(res.totalRecords);
          console.log("This is total records", res.totalRecords);
          this.patchStateWithoutFetch({
            paginator: this._tableState$.value.paginator.recalculatePaginator(
              res.totalRecords
            ),
          });

          if (changeRoute) {
            this.router.navigate([], {
              relativeTo: this.activatedRoute,
              queryParams: this.setupRoute(params),
              queryParamsHandling: "merge",
            });
          }
        }),
        finalize(() => {
          this._isLoading$.next(false);
        })
      )
      .subscribe();
    this._subscriptions.push(request);
  }

  fetchItemsOnly(
    { queryParams = null }: { queryParams?: any } = {
      queryParams: null,
    }
  ) {
    let params = new HttpParams();
    if (queryParams) {
      Object.entries(queryParams).forEach((item: any) => {
        params = params.append(item[0], item[1]);
      });
    }
    const url = `${this.BASE_URL}/${this.GET_ALL_URL}`;
    this._isLoading$.next(true);
    const request = this.http
      .get<T[]>(url, { params })
      .pipe(
        tap((res: T[]) => {
          this._items$.next(res);
        }),
        finalize(() => {
          this._isLoading$.next(false);
        })
      )
      .subscribe();
    this._subscriptions.push(request);
  }

  setupRoute(params: HttpParams) {
    const queryParam = {};
    params
      .toString()
      .split("&")
      .forEach((param) => {
        const splittedParam = param.split("=");
        queryParam[splittedParam[0]] = splittedParam[1];
      });
    return queryParam;
  }

  fetchFromUrl(query: any = null) {
    if (this.router.url.includes("?")) {
      const state: any = {};
      const queryParams = {};
      const params = this.router.url.split("?");
      params.splice(0, 1);

      params
        .join("")
        .split("&")
        .forEach((param) => {
          const splittedParam = param.split("=");
          if (tableStateParams.includes(splittedParam[0])) {
            state[splittedParam[0]] = splittedParam[1];
          } else {
            queryParams[splittedParam[0]] = splittedParam[1];
          }
        });

      console.log("=========== State ============", state);

      this.patchState(
        {
          ...(state.search && { searchTerm: state.search }),
          ...(state.pageNumber &&
            state.pageSize && {
              paginator: new PaginatorState(+state.pageSize, +state.pageNumber),
            }),
          ...(state.sortField &&
            state.sortOrder && {
              sorting: new SortState(state.sortField, state.sortOrder),
            }),
        },
        queryParams
      );
    } else {
      this.fetch({ queryParams: query });
    }
  }

  getPropertyFromUrl(prop: string) {
    const splittedParam = this.router.url.split("&");

    if (!splittedParam.length) {
      return "";
    }

    const keyword = splittedParam.find((item) => item.includes(prop));
    return keyword?.split("=")[1];
  }

  // CREATE
  // server should return the object with ID
  create(item: BaseModel): Observable<string> {
    const url = `${this.BASE_URL}/${this.CREATE_URL}`;
    this._isLoading$.next(true);
    return this.http
      .post<string>(url, item, this.httpOptions)
      .pipe(finalize(() => this._isLoading$.next(false)));
  }

  getItem(id: string | number): Observable<BaseModel> {
    this._isFirstLoading$.next(true);
    const url = `${this.BASE_URL}/${this.GET_ONE_URL}/${id}`;
    return this.http.get<BaseModel>(url).pipe(
      map((res: any) => {
        return res;
      }),
      finalize(() => this._isFirstLoading$.next(false))
    );
  }

  // UPDATE
  update(item: BaseModel): Observable<boolean> {
    const url = `${this.BASE_URL}/${this.UPDATE_URL}/${item.id}`;
    this._isLoading$.next(true);
    return this.http
      .put<boolean>(url, item, this.httpOptions)
      .pipe(finalize(() => this._isLoading$.next(false)));
  }

  // DELETE
  delete(id: any): Observable<any> {
    this._isLoading$.next(true);
    const url = `${this.BASE_URL}/${this.DELETE_URL}/${id}`;
    return this.http
      .delete(url)
      .pipe(finalize(() => this._isLoading$.next(false)));
  }

  request(
    url: string,
    requestType: any = RequestTypes.GET,
    data: any = null
  ): Observable<any> {
    this._isRequestLoading$.next(true);
    return this.http[requestType](
      url,
      requestType === RequestTypes.GET ? this.httpOptions : data
    ).pipe(finalize(() => this._isRequestLoading$.next(false)));
  }

  public setDefaults() {
    this.patchStateWithoutFetch({ filter: {} });
    this.patchStateWithoutFetch({
      sorting: new SortState(this.DEFAULT_SORT_FIELD || ""),
    });
    this.patchStateWithoutFetch({ searchTerm: "" });
    this.patchStateWithoutFetch({
      paginator: new PaginatorState(),
    });
    this._isFirstLoading$.next(true);
    this._isLoading$.next(true);
    this._tableState$.next(DEFAULT_STATE);
  }

  // Base Methods
  public patchState(
    patch: Partial<ITableState> | null,
    queryParams = null,
    changeRoute?: boolean
  ) {
    this.patchStateWithoutFetch(patch);
    this.fetch({ queryParams, changeRoute: changeRoute });
  }

  public patchStateWithoutFetch(patch: Partial<ITableState>) {
    const newState = { ...this._tableState$.value, ...patch };

    console.log("========== Old State ===========", this._tableState$.value);
    console.log("========== Patched State ===========", patch);
    console.log("========== New State ===========", newState);
    this._tableState$.next(newState);
  }

  public extractQueryParams(obj) {
    const params = {};
    Object.entries(obj).forEach((item: any) => {
      if (item[1] !== undefined && item[1] !== null) {
        params[item[0]] = item[1];
      }
    });

    return params;
  }
}
